import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Bell } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeAgo = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export const NotificationsBell = ({ compact = false, direction = 'up' }) => {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const toastedRef = useRef(new Set());
  const firstPollRef = useRef(true);

  const unread = items.filter(n => !n.read).length;

  useEffect(() => {
    if (!isAuthenticated) return;
    let alive = true;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/notifications`, { withCredentials: true });
        if (!alive) return;
        const list = r.data.notifications || [];
        setItems(list);
        // Instant ping: toast NEW unread key-failure alerts the moment they appear
        list.forEach(n => {
          if (n.kind === 'key_failure' && !n.read && !toastedRef.current.has(n.notification_id)) {
            toastedRef.current.add(n.notification_id);
            if (!firstPollRef.current) toast.error(n.title, { description: n.message, duration: 12000 });
          }
        });
        firstPollRef.current = false;
      } catch { /* signed out / offline */ }
    };
    poll();
    const t = setInterval(poll, 45000);
    return () => { alive = false; clearInterval(t); };
  }, [isAuthenticated]);

  if (!isAuthenticated) return null;

  const openPanel = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      const ids = items.filter(n => !n.read).map(n => n.notification_id);
      try {
        await axios.post(`${API}/notifications/mark_read`, { ids }, { withCredentials: true });
        setItems(prev => prev.map(n => ({ ...n, read: true })));
      } catch { /* non-fatal */ }
    }
  };

  return (
    <div className="relative">
      <button
        onClick={openPanel}
        aria-label="Notifications"
        title="Notifications"
        className={`relative rounded-full flex items-center justify-center text-zinc-400 hover:text-[#06B6D4] hover:bg-white/5 ${compact ? 'w-8 h-8' : 'w-9 h-9'}`}
        data-testid="notifications-bell"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notifications-badge">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className={`absolute right-0 w-80 max-h-96 overflow-y-auto bg-[#0A0A0C] border border-[#1E1E24] rounded-xl shadow-2xl z-50 p-2 ${direction === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'}`} data-testid="notifications-panel">
          <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 px-2 py-1">Notifications</p>
          {items.length === 0 ? (
            <p className="text-xs text-zinc-500 px-2 py-3">Nothing yet.</p>
          ) : items.map(n => (
            <div key={n.notification_id} className={`px-2 py-2 rounded-lg mb-1 ${n.kind === 'key_failure' ? 'bg-red-500/5 border border-red-500/20' : 'hover:bg-white/5'}`} data-testid={`notification-${n.notification_id}`}>
              <p className="text-xs font-bold text-white leading-snug">{n.title}</p>
              <p className="text-[11px] text-zinc-400 leading-snug mt-0.5">{n.message}</p>
              <p className="text-[10px] text-zinc-600 mt-1 font-mono">{timeAgo(n.created_at)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
